// cSpell:ignore alertable
import { CodeableConcept, Observation } from 'fhir/r4b';
import { DateTime } from 'luxon';
import {
  FHIRObservationInterpretation,
  FHIRObservationInterpretationCodesMap,
  VitalAlertCriticality,
  VitalFieldNames,
} from '../../types/api/chart-data/chart-data.constants';
import { VitalsObservationDTO } from '../../types/api/chart-data/chart-data.types';
import { GetVitalsResponseData } from '../../types/api/chart-data/get-vitals.types';
import { AlertRule, AlertThreshold, VitalsSchema } from './config-schema';

export const convertVitalsListToMap = (list: VitalsObservationDTO[]): GetVitalsResponseData => {
  const vitalsMap: Partial<GetVitalsResponseData> = {};

  list.forEach((vital) => {
    // Ensure the field is a valid VitalFieldNames
    if (Object.values(VitalFieldNames).includes(vital.field)) {
      const current = (vitalsMap as any)[vital.field] ?? [];
      current.push(vital);
      (vitalsMap as any)[vital.field] = current;
    } else {
      console.log('field is not a valid VitalFieldNames:', vital.field, Object.values(VitalFieldNames));
    }
  });

  // Ensure all required fields are present and not undefined
  const fullVitalsMap: GetVitalsResponseData = {} as GetVitalsResponseData;
  Object.values(VitalFieldNames).forEach((field) => {
    (fullVitalsMap as any)[field] = (vitalsMap as any)[field] ?? [];
  });
  return fullVitalsMap;
};

export const getVitalDTOCriticalityFromObservation = (observation: Observation): VitalAlertCriticality | undefined => {
  const interpretations = observation?.interpretation ?? [];
  if (observation.component && observation.component.length > 0) {
    const componentInterpretations = observation.component.flatMap((comp) => comp.interpretation ?? []);
    interpretations.push(...componentInterpretations);
  }

  const mergeInterpretations = (interpretations: CodeableConcept[]): VitalAlertCriticality | undefined => {
    if (
      interpretations.some(
        (item) =>
          item.coding?.some(
            (code) =>
              code.code === FHIRObservationInterpretation.CriticalLow ||
              code.code === FHIRObservationInterpretation.CriticalHigh
          )
      )
    ) {
      return VitalAlertCriticality.Critical;
    }
    if (
      interpretations.some(
        (item) =>
          item.coding?.some(
            (code) =>
              code.code === FHIRObservationInterpretation.AbnormalLow ||
              code.code === FHIRObservationInterpretation.AbnormalHigh
          )
      )
    ) {
      return VitalAlertCriticality.Abnormal;
    }
    return undefined;
  };
  return mergeInterpretations(interpretations);
};

interface CheckForAbnormalValueInput {
  patientDOB: string;
  patientSex: string | undefined; // optional, used for ageSexFunction rules
  vitalsObservation: VitalsObservationDTO;
  config: VitalsSchema;
  asOfDate?: string;
}

const resolveAsOf = (asOfDate: string | undefined): DateTime => {
  if (!asOfDate) return DateTime.now();
  const parsed = DateTime.fromISO(asOfDate);
  return parsed.isValid ? parsed : DateTime.now();
};

export const getVitalObservationAlertLevel = (input: CheckForAbnormalValueInput): VitalAlertCriticality | undefined => {
  const { patientDOB: dob, patientSex, vitalsObservation, config, asOfDate } = input;
  const vitalsKey = vitalsObservation.field;
  const asOf = resolveAsOf(asOfDate);
  const patientAgeInMonths = asOf.diff(DateTime.fromISO(dob), 'months').months;

  const getVitalCriticalityFromAlertLevels = (
    alertLevels: FHIRObservationInterpretation[]
  ): VitalAlertCriticality | undefined => {
    const someCritical = alertLevels.some(
      (level) =>
        level === FHIRObservationInterpretation.CriticalHigh || level === FHIRObservationInterpretation.CriticalLow
    );
    const someAbnormal = alertLevels.some(
      (level) =>
        level === FHIRObservationInterpretation.AbnormalHigh || level === FHIRObservationInterpretation.AbnormalLow
    );

    if (someCritical) {
      return VitalAlertCriticality.Critical;
    }
    if (someAbnormal) {
      return VitalAlertCriticality.Abnormal;
    }
    return undefined;
  };

  const rulesOrComponents = findRulesForVitalsKeyAndDOB(vitalsKey as VitalFieldNames, dob, config, asOf);

  const { type } = rulesOrComponents;
  if (type === 'rules') {
    const { rules } = rulesOrComponents;
    const alertLevels = getAlertLevels({
      observation: vitalsObservation,
      rules,
      patientAgeInMonths,
      patientSex,
    });
    return getVitalCriticalityFromAlertLevels(alertLevels);
  } else {
    // currently we do nothing in the ui to identify which component is the source of the alert,
    // so combining all components into a single criticality like this does the trick
    const { components } = rulesOrComponents;
    const result: { [componentName: string]: FHIRObservationInterpretation[] } = {};
    Object.entries(components).forEach(([name, componentRules]) => {
      const alertLevels = getAlertLevels({
        observation: vitalsObservation,
        rules: componentRules,
        patientAgeInMonths,
        componentName: name,
        patientSex,
      });
      result[name] = alertLevels;
    });
    // Combine all interpretations from components
    const combinedAlertLevels = Object.values(result).flat();
    return getVitalCriticalityFromAlertLevels(combinedAlertLevels);
  }
};

export const getVitalObservationFhirInterpretations = (
  input: CheckForAbnormalValueInput
): CodeableConcept[] | undefined => {
  const { patientDOB: dob, patientSex, vitalsObservation, config, asOfDate } = input;
  const vitalsKey = vitalsObservation.field;
  const asOf = resolveAsOf(asOfDate);
  const patientAgeInMonths = asOf.diff(DateTime.fromISO(dob), 'months').months;
  const rulesOrComponents = findRulesForVitalsKeyAndDOB(vitalsKey as VitalFieldNames, dob, config, asOf);
  // console.log('rules for', vitalsKey, rulesOrComponents.length);
  const { type } = rulesOrComponents;
  console.log('vitals key:', vitalsKey, 'type:', type, patientSex);
  if (type === 'rules') {
    const { rules } = rulesOrComponents;
    const alertLevels = getAlertLevels({
      observation: vitalsObservation,
      rules,
      patientAgeInMonths,
      patientSex,
    });
    console.log('alert levels:', alertLevels);
    return getAlertLevelsFromInterpretations(alertLevels);
  }
  return undefined;
};

export const getVitalObservationFhirComponentInterpretations = (
  input: CheckForAbnormalValueInput
): { [componentName: string]: CodeableConcept[] } | undefined => {
  const { patientDOB: dob, patientSex, vitalsObservation, config, asOfDate } = input;
  const vitalsKey = vitalsObservation.field;

  const asOf = resolveAsOf(asOfDate);
  const patientAgeInMonths = asOf.diff(DateTime.fromISO(dob), 'months').months;
  const rulesOrComponents = findRulesForVitalsKeyAndDOB(vitalsKey as VitalFieldNames, dob, config, asOf);
  const { type } = rulesOrComponents;
  if (type === 'components') {
    const { components } = rulesOrComponents;
    const result: { [componentName: string]: CodeableConcept[] } = {};
    Object.entries(components).forEach(([name, componentRules]) => {
      const alertLevels = getAlertLevels({
        observation: vitalsObservation,
        rules: componentRules,
        patientAgeInMonths,
        componentName: name,
        patientSex,
      });
      result[name] = getAlertLevelsFromInterpretations(alertLevels) ?? [];
    });
    return result;
  }
  return undefined;
};

const getAlertLevelsFromInterpretations = (alertLevels: FHIRObservationInterpretation[]): CodeableConcept[] => {
  let deduped = Array.from(new Set(alertLevels));
  // Remove AbnormalHigh if CriticalHigh is present
  if (deduped.includes(FHIRObservationInterpretation.CriticalHigh)) {
    deduped = deduped.filter((level) => level !== FHIRObservationInterpretation.AbnormalHigh);
  }
  // Remove AbnormalLow if CriticalLow is present
  if (deduped.includes(FHIRObservationInterpretation.CriticalLow)) {
    deduped = deduped.filter((level) => level !== FHIRObservationInterpretation.AbnormalLow);
  }

  // always remove Normal
  deduped = deduped.filter((level) => level !== FHIRObservationInterpretation.Normal);

  return deduped.map((level) => {
    return FHIRObservationInterpretationCodesMap[level];
  });
};

const findRulesForVitalsKeyAndDOB = (
  key: VitalFieldNames,
  dob: string,
  config: VitalsSchema,
  asOf: DateTime = DateTime.now()
):
  | { type: 'rules'; rules: AlertRule[] }
  | { type: 'components'; components: { [componentName: string]: AlertRule[] } } => {
  if (key === 'vital-last-menstrual-period' || key === 'vital-bmi') {
    return { type: 'rules', rules: [] };
  }

  const dateOfBirth = DateTime.fromISO(dob);
  const alertThresholds: AlertThreshold[] = config[key]?.alertThresholds ?? [];
  const alertComponents: { [componentName: string]: AlertRule[] } = {};
  if (key === 'vital-blood-pressure' || key === 'vital-vision') {
    // For blood pressure, we need to check components
    const components = config[key]?.components;
    if (components) {
      Object.entries(components).forEach(([name, component]) => {
        alertComponents[name] = getRulesForPatientDOB(component.alertThresholds ?? [], dateOfBirth, asOf);
      });
    }
    return { type: 'components', components: alertComponents };
  }
  return { type: 'rules', rules: getRulesForPatientDOB(alertThresholds, dateOfBirth, asOf) };
};

const getRulesForPatientDOB = (
  alertThresholds: AlertThreshold[],
  dateOfBirth: DateTime,
  asOf: DateTime
): AlertRule[] => {
  const rules = alertThresholds
    .filter((threshold) => {
      const { minAge, maxAge } = threshold;
      let inRange = true;
      if (minAge) {
        const patientAge = asOf.diff(dateOfBirth, minAge.unit)[minAge.unit];
        inRange &&= patientAge >= minAge.value;
      }
      if (maxAge) {
        const patientAge = asOf.diff(dateOfBirth, maxAge.unit)[maxAge.unit];
        inRange &&= patientAge < maxAge.value;
      }
      return inRange;
    })
    .flatMap((threshold) => threshold.rules ?? []);
  return rules;
};

interface AlertableValuesInput {
  observation: VitalsObservationDTO;
  rules: AlertRule[];
  patientAgeInMonths: number;
  patientSex: string | undefined; // for ageSexFunction rules, should always be available, so we require specifying undefined if not available, or explicitly irrelevant
  componentName?: string;
}

const getAlertLevels = (input: AlertableValuesInput): FHIRObservationInterpretation[] => {
  const { observation, rules, patientAgeInMonths, patientSex, componentName } = input;
  if (observation.field === VitalFieldNames.VitalLastMenstrualPeriod) {
    return [];
  }
  if (observation.field === VitalFieldNames.VitalVision) {
    return [];
  }
  let value: number | undefined = observation.value;
  if (observation.field === VitalFieldNames.VitalBloodPressure) {
    // do a blood pressure-specific check on components
    if (componentName === 'systolic-pressure') {
      value = observation.systolicPressure;
    }
    if (componentName === 'diastolic-pressure') {
      value = observation.diastolicPressure;
    }
  }

  if (value === undefined || value === null || typeof value !== 'number') {
    console.warn('Vital value is not a number:', value);
    return [];
  }

  return rules
    .map((rule) => getAlertLevel({ rule, numericValue: value, patientAgeInMonths, patientSex }))
    .filter((level) => level !== FHIRObservationInterpretation.Normal);
};

interface EvalRuleProps {
  numericValue: number;
  rule: AlertRule;
  patientAgeInMonths?: number;
  patientSex?: string;
}
const getAlertLevel = (input: EvalRuleProps): FHIRObservationInterpretation => {
  const { numericValue: value, rule, patientAgeInMonths, patientSex } = input;
  let thresholdValue: number | undefined;

  if ('value' in rule && typeof rule.value === 'number') {
    thresholdValue = rule.value;
  } else if ('ageFunction' in rule && typeof rule.ageFunction === 'function') {
    if (patientAgeInMonths === undefined) {
      console.warn('Rule has an ageFunction but no patientAgeInMonths provided:', rule);
      return FHIRObservationInterpretation.Normal;
    }
    thresholdValue = rule.ageFunction(patientAgeInMonths / 12.0);
  } else if ('ageSexFunction' in rule && typeof rule.ageSexFunction === 'function') {
    if (patientAgeInMonths === undefined) {
      console.warn('Rule has an ageSexFunction but no patientAgeInMonths provided:', rule);
      return FHIRObservationInterpretation.Normal;
    }
    if (patientSex === undefined) {
      console.warn('Rule has an ageSexFunction but no patientSex provided:', rule);
      return FHIRObservationInterpretation.Normal;
    }
    let patientSexValidated: 'male' | 'female' | undefined;
    if (patientSex === 'male' || patientSex === 'female') {
      patientSexValidated = patientSex;
    }
    if (!patientSexValidated) {
      console.warn('Alert thresholds are not defined for patientSex:', patientSex);
      return FHIRObservationInterpretation.Normal;
    }
    thresholdValue = rule.ageSexFunction(patientAgeInMonths, patientSexValidated);
  }

  if (thresholdValue === undefined) {
    console.warn('Rule does not have a value or ageFunction:', rule);
    return FHIRObservationInterpretation.Normal;
  }

  const ruleCriticality = rule.criticality;
  if (rule.type === 'min') {
    if (value <= thresholdValue) {
      if (ruleCriticality === VitalAlertCriticality.Critical) {
        return FHIRObservationInterpretation.CriticalLow;
      }
      if (ruleCriticality === VitalAlertCriticality.Abnormal) {
        return FHIRObservationInterpretation.AbnormalLow;
      }
    }
  }
  if (rule.type === 'max') {
    if (value >= thresholdValue) {
      if (ruleCriticality === VitalAlertCriticality.Critical) {
        return FHIRObservationInterpretation.CriticalHigh;
      }
      if (ruleCriticality === VitalAlertCriticality.Abnormal) {
        return FHIRObservationInterpretation.AbnormalHigh;
      }
    }
  }
  return FHIRObservationInterpretation.Normal;
};

export const getAbnormalVitals = (encounterVitals?: GetVitalsResponseData): GetVitalsResponseData => {
  const entries = Object.entries(encounterVitals || {})
    .map(([key, values]) => (Array.isArray(values) ? [key, values.filter((v) => !!v.alertCriticality)] : [key, []]))
    .filter(([, values]) => values.length > 0);

  return Object.fromEntries(entries);
};
